import React, { useState, useEffect } from "react";
import {
  Box, Typography, Card, CardContent, TextField, Button,
  Switch, CircularProgress, Alert, Snackbar,
} from "@mui/material";
import SettingsRoundedIcon     from "@mui/icons-material/SettingsRounded";
import ShieldRoundedIcon       from "@mui/icons-material/ShieldRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import SecurityRoundedIcon     from "@mui/icons-material/SecurityRounded";
import CheckCircleRoundedIcon  from "@mui/icons-material/CheckCircleRounded";
import ProtectionNav from "./ProtectionNav";
import { getData, putData } from "../../services/apiService";
import { API_ENDPOINTS } from "../../services/apiService";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
  teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
  emerald: "#059669", emeraldLt: "#D1FAE5",
  amber:   "#D97706", amberLt:  "#FEF3C7",
  indigo:  "#4F46E5", indigoLt: "#EEF2FF",
  orange:  "#FF7900", orangeLt: "#FFF0E0",
  rose:    "#E11D48", roseLt:   "#FFE4E6",
  bg:      "#F7FAFC", border: "#E2E8F0",
  text:    "#0F172A", muted:  "#64748B",
};

// ── Config key definitions ────────────────────────────────────────────────
const CONFIG_GROUPS = [
  {
    key: "tiers",
    title: "Plan Tiers",
    subtitle: "Premium rates and coverage limits per protection tier",
    icon: <ShieldRoundedIcon />,
    accent: T.indigo,
    fields: [
      // Basic
      { key:"cpm_basic_rate_pct",     label:"Basic Rate (%)",    type:"number", step:0.1, tier:"basic",    desc:"Premium as % of declared value" },
      { key:"cpm_basic_coverage_pct", label:"Basic Coverage (%)",type:"number", step:5,   tier:"basic",    desc:"% of declared value covered" },
      // Standard
      { key:"cpm_standard_rate_pct",     label:"Standard Rate (%)",    type:"number", step:0.1, tier:"standard", desc:"" },
      { key:"cpm_standard_coverage_pct", label:"Standard Coverage (%)",type:"number", step:5,   tier:"standard", desc:"" },
      // Premium
      { key:"cpm_premium_rate_pct",     label:"Premium Rate (%)",    type:"number", step:0.1, tier:"premium",  desc:"" },
      { key:"cpm_premium_coverage_pct", label:"Premium Coverage (%)",type:"number", step:5,   tier:"premium",  desc:"" },
    ],
  },
  {
    key: "limits",
    title: "Global Limits",
    subtitle: "Min/max premium amounts and declared value caps",
    icon: <AccountBalanceRoundedIcon />,
    accent: T.teal,
    fields: [
      { key:"cpm_min_premium",                  label:"Min Premium (₹)",                type:"number", desc:"Floor for any premium amount" },
      { key:"cpm_max_premium",                  label:"Max Premium (₹)",                type:"number", desc:"Ceiling for any premium amount" },
      { key:"cpm_premium_max_declared_value",   label:"Max Declared Value — Premium (₹)",type:"number", desc:"Declared value cap for Premium tier" },
      { key:"cpm_max_coverage_limit",           label:"Max Coverage Limit — All Tiers (₹)",type:"number", desc:"Hard ceiling on payout across all tiers" },
    ],
  },
  {
    key: "thresholds",
    title: "Fund Thresholds",
    subtitle: "Auto-pause tiers when fund balance drops below these levels",
    icon: <AccountBalanceRoundedIcon />,
    accent: T.amber,
    fields: [
      { key:"cpm_fund_pause_premium_threshold", label:"Pause Premium Tier Below (₹)",  type:"number", desc:"Pause only the Premium tier at this balance" },
      { key:"cpm_fund_pause_all_threshold",     label:"Pause All Tiers Below (₹)",     type:"number", desc:"Pause all tiers — emergency floor" },
    ],
  },
  {
    key: "risk",
    title: "Risk Controls",
    subtitle: "Fraud detection and repeat-claimant flags",
    icon: <SecurityRoundedIcon />,
    accent: T.rose,
    fields: [
      { key:"cpm_claim_max_payout_premium_ratio",label:"Flag Payout > N× Premium",     type:"number", desc:"Flag claim if payout exceeds N times the premium" },
      { key:"cpm_repeat_claimant_threshold",     label:"Repeat Claimant Threshold",    type:"number", desc:"Number of claims within window to flag" },
      { key:"cpm_repeat_claimant_months",        label:"Repeat Claimant Window (months)",type:"number", desc:"Rolling window for repeat-claimant check" },
    ],
  },
];

// ── Tier colours ──────────────────────────────────────────────────────────
const TIER_CFG = {
  basic:    { label:"Basic",    accent:T.teal,    bg:T.tealLt    },
  standard: { label:"Standard", accent:T.indigo,  bg:T.indigoLt  },
  premium:  { label:"Premium",  accent:T.amber,   bg:T.amberLt   },
};

// ── Section card ──────────────────────────────────────────────────────────
const SectionCard = ({ group, values, onChange }) => (
  <Card sx={{ borderRadius:"16px", border:`1px solid ${T.border}`, boxShadow:"0 1px 4px rgba(15,23,42,0.06)", mb:3, overflow:"hidden" }}>
    <Box sx={{ height:3, background:`linear-gradient(90deg,${group.accent},${group.accent}66)` }} />
    <CardContent sx={{ p:2.5,"&:last-child":{ pb:2.5 } }}>
      <Box sx={{ display:"flex", alignItems:"center", gap:1.2, mb:2.5 }}>
        <Box sx={{ width:30, height:30, borderRadius:"8px", bgcolor:`${group.accent}18`, display:"flex", alignItems:"center", justifyContent:"center" }}>
          {React.cloneElement(group.icon, { sx:{ fontSize:16, color:group.accent } })}
        </Box>
        <Box>
          <Typography sx={{ fontSize:14, fontWeight:700, color:T.text, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{group.title}</Typography>
          <Typography sx={{ fontSize:12, color:T.muted }}>{group.subtitle}</Typography>
        </Box>
      </Box>

      {/* Group tier fields in pairs if they have tier property */}
      {group.key === "tiers" ? (
        <Box sx={{ display:"flex", flexDirection:"column", gap:2 }}>
          {["basic","standard","premium"].map(tier => {
            const cfg = TIER_CFG[tier];
            const fields = group.fields.filter(f => f.tier === tier);
            return (
              <Box key={tier} sx={{ p:2, borderRadius:"12px", bgcolor:cfg.bg, border:`1px solid ${cfg.accent}22` }}>
                <Box sx={{ display:"inline-flex", px:1.2, py:0.3, bgcolor:"white", borderRadius:"7px", mb:1.5, border:`1px solid ${cfg.accent}33` }}>
                  <Typography sx={{ fontSize:11, fontWeight:800, color:cfg.accent, textTransform:"uppercase", letterSpacing:"0.4px" }}>{cfg.label}</Typography>
                </Box>
                <Box sx={{ display:"flex", gap:2, flexWrap:"wrap" }}>
                  {fields.map(f => (
                    <TextField key={f.key} label={f.label} type="number" size="small"
                      value={values[f.key]??""} onChange={e=>onChange(f.key,e.target.value)}
                      inputProps={{ step:f.step||1 }}
                      helperText={f.desc}
                      sx={{ flex:"1 1 200px", "& .MuiOutlinedInput-root":{ borderRadius:"10px", bgcolor:"white" }, "& .MuiFormLabel-root":{ fontSize:13 } }}
                    />
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box sx={{ display:"flex", gap:2, flexWrap:"wrap" }}>
          {group.fields.map(f => (
            <TextField key={f.key} label={f.label} type="number" size="small"
              value={values[f.key]??""} onChange={e=>onChange(f.key,e.target.value)}
              inputProps={{ step:f.step||1 }}
              helperText={f.desc}
              sx={{ flex:"1 1 240px", "& .MuiOutlinedInput-root":{ borderRadius:"10px" }, "& .MuiFormLabel-root":{ fontSize:13 } }}
            />
          ))}
        </Box>
      )}
    </CardContent>
  </Card>
);

// ── Main component ────────────────────────────────────────────────────────
export default function ProtectionConfig() {
  const [values,  setValues]  = useState({});
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [snack,   setSnack]   = useState({ open:false, msg:"", sev:"success" });
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await getData("/api/charges/getAllCharges");
        const arr = res?.message?.data || res?.result || [];
        if (Array.isArray(arr)) {
          const map = {};
          arr.forEach(c => {
            if (c.key?.startsWith("cpm_")) map[c.key] = parseFloat(c.value_numeric)||c.value_text||"";
          });
          setValues(map);
          if (map.cpm_enabled !== undefined) setEnabled(parseFloat(map.cpm_enabled||0)===1);
        }
      } catch(e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  const handleChange = (key, val) => setValues(p => ({ ...p, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const configs = [
        { key:"cpm_enabled", value_numeric: enabled ? 1 : 0 },
        ...Object.entries(values).map(([key,val]) => ({
          key, value_numeric: parseFloat(val)||0,
        })),
      ];
      await putData(API_ENDPOINTS.updateProtectionConfig, { configs });
      setSnack({ open:true, msg:"Configuration saved successfully!", sev:"success" });
    } catch(e) {
      console.error(e);
      setSnack({ open:true, msg:"Failed to save configuration.", sev:"error" });
    }
    setSaving(false);
  };

  if (loading) return (
    <Box sx={{ bgcolor:T.bg, minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <CircularProgress sx={{ color:T.teal }} />
    </Box>
  );

  // Preview calculation (₹50,000 declared value)
  const preview = ["basic","standard","premium"].map(tier => {
    const rate   = parseFloat(values[`cpm_${tier}_rate_pct`]||0);
    const covPct = parseFloat(values[`cpm_${tier}_coverage_pct`]||100);
    const raw    = 50000 * (rate/100);
    const min    = parseFloat(values.cpm_min_premium||50);
    const max    = parseFloat(values.cpm_max_premium||6000);
    const premium  = Math.min(Math.max(raw, min), max);
    const coverage = Math.round(50000 * (covPct/100));
    return { tier, rate, covPct, premium, coverage, ...TIER_CFG[tier] };
  });

  return (
    <Box sx={{ bgcolor:T.bg, minHeight:"100vh", pb:6 }}>

      {/* Hero header */}
      <Box sx={{ background:`linear-gradient(135deg,${T.teal} 0%,${T.tealDk} 100%)`, borderRadius:"20px", p:3, mb:3,
        display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:2,
        boxShadow:"0 4px 20px rgba(0,124,132,0.2)" }}>
        <Box sx={{ display:"flex", alignItems:"center", gap:2 }}>
          <Box sx={{ width:46,height:46,borderRadius:"12px",bgcolor:"rgba(255,255,255,0.18)",border:"1px solid rgba(255,255,255,0.25)",display:"flex",alignItems:"center",justifyContent:"center" }}>
            <SettingsRoundedIcon sx={{ fontSize:24,color:"#fff" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize:22,fontWeight:800,color:"#fff",fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Cargo Protection</Typography>
            <Typography sx={{ fontSize:13,color:"rgba(255,255,255,0.75)",mt:0.3 }}>Configure plan rates, limits, and fund thresholds</Typography>
          </Box>
        </Box>
        {/* Global toggle */}
        <Box sx={{ display:"flex", alignItems:"center", gap:1.5, bgcolor:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.25)", borderRadius:"14px", px:2, py:1.2 }}>
          <Typography sx={{ fontSize:13, fontWeight:600, color:"#fff" }}>Protection</Typography>
          <Switch
            checked={enabled}
            onChange={e => setEnabled(e.target.checked)}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color:T.emerald },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor:T.emerald },
              "& .MuiSwitch-track": { bgcolor:"rgba(255,255,255,0.3)" },
            }}
          />
          <Box sx={{ px:1, py:0.3, bgcolor:enabled?T.emeraldLt:T.roseLt, borderRadius:"7px" }}>
            <Typography sx={{ fontSize:11, fontWeight:800, color:enabled?T.emerald:T.rose }}>
              {enabled?"ENABLED":"DISABLED"}
            </Typography>
          </Box>
        </Box>
      </Box>

      <ProtectionNav activeTab={3} />

      {/* Config sections */}
      {CONFIG_GROUPS.map(group => (
        <SectionCard key={group.key} group={group} values={values} onChange={handleChange} />
      ))}

      {/* Premium preview */}
      <Card sx={{ borderRadius:"16px", border:`1px solid ${T.border}`, boxShadow:"0 1px 4px rgba(15,23,42,0.06)", mb:3 }}>
        <CardContent sx={{ p:2.5,"&:last-child":{ pb:2.5 } }}>
          <Box sx={{ display:"flex", alignItems:"center", gap:1.2, mb:2.5 }}>
            <Box sx={{ width:30,height:30,borderRadius:"8px",bgcolor:T.tealLt,display:"flex",alignItems:"center",justifyContent:"center" }}>
              <ShieldRoundedIcon sx={{ fontSize:16,color:T.teal }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize:14,fontWeight:700,color:T.text,fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Premium Calculation Preview</Typography>
              <Typography sx={{ fontSize:12,color:T.muted }}>Example: ₹50,000 declared value</Typography>
            </Box>
          </Box>
          <Box sx={{ display:"flex", gap:2, flexWrap:"wrap" }}>
            {preview.map(p => (
              <Box key={p.tier} sx={{ flex:"1 1 180px", p:2, borderRadius:"14px", bgcolor:p.bg, border:`1px solid ${p.accent}22`, textAlign:"center" }}>
                <Box sx={{ display:"inline-flex", px:1.5, py:0.4, bgcolor:"white", borderRadius:"8px", mb:1.5, border:`1px solid ${p.accent}33` }}>
                  <Typography sx={{ fontSize:11,fontWeight:800,color:p.accent,textTransform:"uppercase",letterSpacing:"0.4px" }}>{p.label}</Typography>
                </Box>
                <Typography sx={{ fontSize:28,fontWeight:800,color:p.accent,lineHeight:1.1,fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                  ₹{Math.round(p.premium).toLocaleString("en-IN")}
                </Typography>
                <Typography sx={{ fontSize:11,color:p.accent,opacity:0.8,mt:0.4 }}>premium</Typography>
                <Box sx={{ mt:1.5, pt:1.5, borderTop:`1px solid ${p.accent}22` }}>
                  <Typography sx={{ fontSize:12,color:T.muted }}>Covers up to</Typography>
                  <Typography sx={{ fontSize:14,fontWeight:700,color:T.text }}>₹{p.coverage.toLocaleString("en-IN")}</Typography>
                  <Typography sx={{ fontSize:11,color:T.muted }}>{p.rate}% rate · {p.covPct}% coverage</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Save button — sticky bottom */}
      <Box sx={{ display:"flex", justifyContent:"flex-end", gap:2 }}>
        <Button variant="contained" disableElevation onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckCircleRoundedIcon />}
          sx={{ borderRadius:"12px", textTransform:"none", fontWeight:700, fontSize:14, px:4, py:1.4, bgcolor:T.teal, "&:hover":{ bgcolor:T.tealDk }, boxShadow:`0 4px 14px rgba(0,124,132,0.3)` }}>
          {saving ? "Saving…" : "Save Configuration"}
        </Button>
      </Box>

      {/* Snackbar */}
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={()=>setSnack(s=>({...s,open:false}))}
        anchorOrigin={{ vertical:"top", horizontal:"center" }}>
        <Alert severity={snack.sev} variant="filled" onClose={()=>setSnack(s=>({...s,open:false}))} sx={{ borderRadius:"12px" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
